import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CONSENT_CHANGED_EVENT, OPEN_CONSENT_EVENT, readConsent, writeConsent, withdrawsConsent, NO_CONSENT, FULL_CONSENT } from "./utils/consent.js";
import { applyConsent, deleteGoogleCookies, pushPageView } from "./utils/tagManager.js";

const DATENSCHUTZ_PATH = { de: "datenschutz", en: "privacy" };

/**
 * Re-exported for the footer, which has imported it from here since before
 * the constant moved into consent.js. A plain DOM event rather than shared
 * React state because the footer lives in the hydrated #root tree and this
 * banner in its own createRoot() — there is no common provider between the
 * two, and adding one just to carry a single "open" signal would mean
 * moving the banner back into #root, which is exactly what its own
 * container exists to avoid (see the comment on the component below).
 */
export { OPEN_CONSENT_EVENT };

const cookieBannerStyle = {
  position: "fixed",
  bottom: 0,
  left: 0,
  width: "100%",
  zIndex: 999,
  background: "var(--color-bg-raised)",
  borderTop: "1px solid rgba(206,149,50,0.35)",
  boxShadow: "0 -8px 24px rgba(0,0,0,0.35)",
  padding: "20px 32px",
  display: "flex",
  alignItems: "center",
  gap: "24px",
  flexWrap: "wrap",
  fontFamily: "inherit",
};

const cookieContentStyle = {
  flex: "1 1 320px",
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.6,
  color: "var(--color-cream-soft)",
};

const cookieAcceptStyle = {
  background: "var(--color-gold)",
  color: "var(--color-ink)",
  border: "none",
  borderRadius: "var(--radius-pill)",
  padding: "12px 26px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  margin: 0,
};

const cookieGhostStyle = {
  background: "transparent",
  color: "var(--color-cream)",
  border: "1px solid rgba(254,250,239,0.4)",
  borderRadius: "var(--radius-pill)",
  padding: "12px 26px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  margin: 0,
};

// Deliberately NOT a pill like the two decision buttons: opening the
// options is not itself a consent decision, and a third equally weighted
// button is what makes "accept" and "reject" hard to compare at a glance —
// which is the one thing a consent banner has to get right.
const cookieTextButtonStyle = {
  background: "none",
  border: "none",
  padding: "12px 4px",
  margin: 0,
  fontSize: "14px",
  fontFamily: "inherit",
  fontWeight: 500,
  color: "var(--color-cream)",
  textDecoration: "underline",
  cursor: "pointer",
};

const optionsStyle = {
  flex: "1 1 100%",
  order: 1,
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  margin: 0,
  padding: "16px 0 4px",
  border: "none",
  borderTop: "1px solid rgba(254,250,239,0.15)",
};

const optionsTitleStyle = {
  margin: 0,
  padding: 0,
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--color-cream)",
};

const optionRowStyle = { display: "flex", alignItems: "flex-start", gap: "12px" };

const checkboxStyle = {
  width: "18px",
  height: "18px",
  marginTop: "2px",
  accentColor: "var(--color-gold)",
  flex: "0 0 auto",
  cursor: "pointer",
};

const optionLabelStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--color-cream)",
  cursor: "pointer",
};

// rgba(254,250,239,0.7) on --color-bg-raised (#1a1610) is 8.9:1 — the same
// token the banner text already uses. Anything dimmer than this has failed
// WCAG 1.4.3 on this background before; don't reach for a lower alpha here.
const optionDescStyle = {
  margin: "2px 0 0",
  fontSize: "13px",
  lineHeight: 1.5,
  color: "var(--color-cream-soft)",
};

const alwaysOnStyle = {
  fontSize: "12px",
  fontWeight: 400,
  color: "var(--color-cream-soft)",
};

/**
 * Below ~480px the default layout (long message text wrapping to 3-4
 * lines, then the buttons wrapping to their own stacked lines below)
 * pushed the banner to ~37% of a 375×812 viewport, hiding both hero CTAs
 * behind it on first load. Compacts it on narrow viewports: the long
 * message swaps for a one-line short version (below), smaller type,
 * tighter padding, and the two decision buttons side by side instead of
 * stacked so the whole banner fits in ~2 short rows.
 *
 * The third button ("settings") gets its own full-width row rather than
 * joining that pair: three pill buttons across 375px leaves each one
 * narrower than its own label, and `white-space: nowrap` then overflows
 * the banner instead of wrapping.
 */
const mobileCookieBannerCss = `
  #tl-cookie-banner-message-short {
    display: none;
  }

  @media (max-width: 480px) {
    #tl-cookie-banner {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 8px !important;
      padding: 10px 16px !important;
    }
    #tl-cookie-banner-text {
      /* Desktop's flex: 1 1 320px sets a 320px *height* basis once the
         container switches to column direction here (flex-basis follows
         the main axis, which flips from width to height) — override it
         back to auto or the text block balloons to 320px tall no matter
         how short its content is. */
      flex: 1 1 auto !important;
      font-size: 12.5px !important;
      line-height: 1.4 !important;
    }
    #tl-cookie-banner-message-full {
      display: none;
    }
    #tl-cookie-banner-message-short {
      display: inline;
    }
    #tl-cookie-banner-buttons {
      flex-wrap: wrap !important;
    }
    #tl-cookie-banner-buttons button {
      flex: 1 1 calc(50% - 6px) !important;
      padding: 8px 12px !important;
      font-size: 13px !important;
      white-space: nowrap !important;
    }
    #tl-consent-settings-button {
      flex: 1 1 100% !important;
    }
    #tl-consent-options {
      max-height: 45vh;
      overflow-y: auto;
      padding-top: 12px !important;
    }
  }

  /* Below ~360px the two decision buttons no longer fit side by side:
     "Alle akzeptieren" and "Nur notwendige" are nowrap, and at 147px each
     plus the gap they pushed the banner — and with it the whole page —
     past a 320px screen. One per row keeps them legible and keeps the
     page from scrolling sideways. */
  @media (max-width: 359px) {
    #tl-cookie-banner-buttons button {
      flex: 1 1 100% !important;
    }
  }
`;

function ConsentOption({ id, checked, disabled, onChange, title, description, note }) {
  return (
    <div style={optionRowStyle}>
      <input
        type="checkbox"
        id={`tl-consent-${id}`}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
        style={disabled ? { ...checkboxStyle, cursor: "default" } : checkboxStyle}
      />
      <div>
        <label htmlFor={`tl-consent-${id}`} style={disabled ? { ...optionLabelStyle, cursor: "default" } : optionLabelStyle}>
          {title}
          {note ? <span style={alwaysOnStyle}>{` — ${note}`}</span> : null}
        </label>
        <p style={optionDescStyle}>{description}</p>
      </div>
    </div>
  );
}

/**
 * Rendered into its own createRoot() container (see main.jsx), deliberately
 * outside the hydrated #root tree — whether it is on screen at all depends
 * on document.cookie, which is unknowable at build time, so its first
 * render can never match a prerendered snapshot. Hydrating it against a
 * prerendered guess would just recreate the same mismatch this container
 * exists to avoid. useTranslation() still tracks the app's language
 * correctly here: it subscribes to the shared i18next singleton regardless
 * of which React root it renders in.
 *
 * The stored decision is applied to the tag manager in main.jsx, not here,
 * so it lands before the first page_view rather than one React root later.
 * This component owns the UI and the *changes* to that decision.
 */
export default function CookieConsentRoot() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "de";

  // Lazy initialisers, so the banner's presence is decided in the first
  // render rather than one effect later — this root is never hydrated, so
  // reading document.cookie during render is safe here. A visitor who
  // arrives through the footer button gets their existing choice pre-ticked
  // for the same reason.
  const [choices, setChoices] = useState(() => readConsent() ?? { ...NO_CONSENT });
  const [view, setView] = useState(() => (readConsent() ? "hidden" : "banner"));

  useEffect(() => {
    const open = () => {
      setChoices(readConsent() ?? { ...NO_CONSENT });
      setView("settings");
    };
    window.addEventListener(OPEN_CONSENT_EVENT, open);
    return () => window.removeEventListener(OPEN_CONSENT_EVENT, open);
  }, []);

  function save(next) {
    const previous = readConsent();
    writeConsent(next);
    applyConsent(next);
    // The events map lives in the hydrated tree and has no way to observe
    // this root's state; it listens for this instead (see useConsent.js).
    window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));

    // Taking a permission away needs more than a consent signal: the
    // container is already loaded and keeps its tags in memory, and
    // whatever Google stored is still on the device. Clearing the cookies
    // and reloading is the only honest way back to a denied state — which
    // is also why the footer's own button used to reload unconditionally.
    if (withdrawsConsent(previous, next)) {
      deleteGoogleCookies();
      window.location.reload();
      return;
    }

    // Consent Mode holds the page_view that main.jsx already pushed for at
    // most wait_for_update (500ms) before letting the tag run denied, so a
    // visitor who reads the banner first and then accepts would otherwise
    // have their current page missing from the session entirely.
    if (next.analytics && !previous?.analytics) pushPageView(window.location.pathname);

    setView("hidden");
  }

  if (view === "hidden") return null;

  const showOptions = view === "settings";

  return (
    <div role="region" aria-label={t("cookieConsent.regionLabel")}>
      <style>{mobileCookieBannerCss}</style>
      <div id="tl-cookie-banner" style={cookieBannerStyle}>
        <p id="tl-cookie-banner-text" style={cookieContentStyle}>
          <span id="tl-cookie-banner-message-full">{t("cookieConsent.message")}</span>
          <span id="tl-cookie-banner-message-short">{t("cookieConsent.messageShort")}</span>
          {" "}
          <a href={`/${lang}/${DATENSCHUTZ_PATH[lang]}`} style={{ color: "var(--color-gold)", textDecoration: "underline" }}>
            {t("cookieConsent.privacyLink")}
          </a>
        </p>

        {showOptions && (
          <fieldset id="tl-consent-options" style={optionsStyle}>
            <legend style={optionsTitleStyle}>{t("cookieConsent.settingsTitle")}</legend>
            <ConsentOption
              id="functional"
              checked
              disabled
              title={t("cookieConsent.functionalTitle")}
              note={t("cookieConsent.alwaysOn")}
              description={t("cookieConsent.functionalDesc")}
            />
            <ConsentOption
              id="maps"
              checked={choices.maps}
              onChange={(value) => setChoices((current) => ({ ...current, maps: value }))}
              title={t("cookieConsent.mapsTitle")}
              description={t("cookieConsent.mapsDesc")}
            />
            <ConsentOption
              id="media"
              checked={choices.media}
              onChange={(value) => setChoices((current) => ({ ...current, media: value }))}
              title={t("cookieConsent.mediaTitle")}
              description={t("cookieConsent.mediaDesc")}
            />
            <ConsentOption
              id="analytics"
              checked={choices.analytics}
              onChange={(value) => setChoices((current) => ({ ...current, analytics: value }))}
              title={t("cookieConsent.analyticsTitle")}
              description={t("cookieConsent.analyticsDesc")}
            />
            <ConsentOption
              id="marketing"
              checked={choices.marketing}
              onChange={(value) => setChoices((current) => ({ ...current, marketing: value }))}
              title={t("cookieConsent.marketingTitle")}
              description={t("cookieConsent.marketingDesc")}
            />
          </fieldset>
        )}

        <div
          id="tl-cookie-banner-buttons"
          style={{ display: "flex", gap: "12px", flexWrap: "wrap", flex: "0 0 auto", order: 2 }}
        >
          <button type="button" style={cookieAcceptStyle} onClick={() => save({ ...FULL_CONSENT })}>
            {t("cookieConsent.acceptAll")}
          </button>
          {showOptions ? (
            <button type="button" style={cookieGhostStyle} onClick={() => save(choices)}>
              {t("cookieConsent.saveSelection")}
            </button>
          ) : null}
          <button type="button" style={cookieGhostStyle} onClick={() => save({ ...NO_CONSENT })}>
            {t("cookieConsent.necessaryOnly")}
          </button>
          <button
            type="button"
            id="tl-consent-settings-button"
            style={cookieTextButtonStyle}
            aria-expanded={showOptions}
            aria-controls="tl-consent-options"
            onClick={() => setView(showOptions ? "banner" : "settings")}
          >
            {t("cookieConsent.customize")}
          </button>
        </div>
      </div>
    </div>
  );
}
